import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import useMeta from "@/hooks/useMeta";
import { works } from "@/mocks/works";
import type { WorkType } from "@/types/media";
import { TYPE_LABELS, TYPE_PATHS } from "@/types/media";
import WorkCard from "@/components/base/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";

const PAGE_SIZE = 9;

const TYPE_DESC: Record<WorkType, string> = {
  movie: "院线大片、类型佳作与经典影史作品，发现值得一看的好电影。",
  tv: "热播剧集、口碑好剧与口碑长青剧，追剧不再迷路。",
  variety: "音乐竞演、户外真人秀与生活观察类节目，轻松一刻随时开启。",
};

const SORTS = [
  { key: "latest", label: "最新上映" },
  { key: "popular", label: "热度" },
  { key: "rating", label: "评分" },
] as const;

type SortKey = (typeof SORTS)[number]["key"];

function isValidType(t: string | undefined): t is WorkType {
  return t === "movie" || t === "tv" || t === "variety";
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const valid = isValidType(type);
  const workType: WorkType = valid ? type : "movie";

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const typeLabel = TYPE_LABELS[workType];

  useMeta(
    valid
      ? {
          title: `${typeLabel}频道 - 大象传媒影视推荐`,
          description: `大象传媒${typeLabel}频道，汇集精选${typeLabel}作品，支持地区、年份与热度多维筛选，助你快速找到心仪的${typeLabel}。`,
          keywords: `大象传媒,${typeLabel}推荐,${typeLabel}排行,影视${typeLabel},在线影视`,
        }
      : {
          title: "分类不存在 - 大象传媒",
          description: "您访问的分类不存在，请返回大象传媒首页浏览更多影视内容。",
          keywords: "大象传媒,影视分类",
        }
  );

  const baseList = useMemo(
    () => works.filter((w) => w.type === workType),
    [workType]
  );

  const regions = useMemo(() => {
    const set = new Set(baseList.map((w) => w.region));
    return ["全部", ...Array.from(set)];
  }, [baseList]);

  const years = useMemo(() => {
    const set = new Set(baseList.map((w) => String(w.year)));
    return ["全部", ...Array.from(set).sort((a, b) => Number(b) - Number(a))];
  }, [baseList]);

  // reset filters when route type changes
  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  }, [workType]);

  const filtered = useMemo(() => {
    let list = baseList.filter(
      (w) =>
        (region === "全部" || w.region === region) &&
        (year === "全部" || String(w.year) === year)
    );
    if (sort === "latest") list = [...list].sort((a, b) => b.year - a.year);
    if (sort === "popular") list = [...list].sort((a, b) => b.popularity - a.popularity);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [baseList, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setPage(1);
  };

  if (!valid) {
    return (
      <div className="mx-auto w-full px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="您访问的影视分类不存在或已调整，请通过下方入口浏览其他频道。"
        />
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {(["movie", "tv", "variety"] as WorkType[]).map((t) => (
            <Link
              key={t}
              to={TYPE_PATHS[t]}
              className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              {TYPE_LABELS[t]}频道
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "影视分类" }, { label: typeLabel }]} />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {typeLabel}频道
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {TYPE_DESC[workType]}
        </p>
      </div>

      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground-700">地区</span>
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => {
                  setRegion(r);
                  setPage(1);
                }}
                className={`rounded-full px-3 py-1 text-sm transition-colors ${
                  region === r
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-200 text-foreground-700 hover:bg-background-300"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground-700">年份</span>
          <div className="no-scrollbar flex gap-2 overflow-x-auto">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => {
                  setYear(y);
                  setPage(1);
                }}
                className={`shrink-0 rounded-full px-3 py-1 text-sm transition-colors ${
                  year === y
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-200 text-foreground-700 hover:bg-background-300"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
          <span className="text-sm font-medium text-foreground-700">排序</span>
          <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
            {SORTS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  setSort(s.key);
                  setPage(1);
                }}
                className={`rounded-full px-4 py-1 text-sm transition-colors ${
                  sort === s.key
                    ? "bg-accent-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <span className="ml-auto text-sm text-foreground-500">
            共 {filtered.length} 部作品
          </span>
        </div>
      </div>

      {paged.length === 0 ? (
        <EmptyState
          icon="ri-film-line"
          title="暂无相关影视作品"
          description="当前筛选条件下没有找到匹配的作品，试试切换地区或年份，或者清除筛选条件。"
          actionLabel="清除筛选"
          onAction={resetFilters}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {paged.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}